<template>
	<section id="delivery-options" class="delivery-section">
		<h3 class="h-text">
			<span>другие варианты</span>
			<span class="h-text2"> доставки</span>
		</h3>

		<ul class="options">
			<li
				v-for="opt in options"
				:key="opt.name"
				class="option scroll-animation"
			>
				<div class="info">
					<h4>{{ opt.name }}</h4>
					<p>{{ opt.description }}</p>

					<ul class="values">
						<li>
							<UIShIcon name="cny" class="icon" />
							<span>{{ opt.price }}</span>
						</li>
						<li>
							<UIShIcon name="time" class="icon" />
							<span>{{ opt.time }}</span>
						</li>
					</ul>

					<UIShLink
						:to="opt.link"
						external
						target="_blank"
						class="link"
						md
						v-on="opt.events"
					>
						<UIShButton
							md
							:xl="1920"
							label="Рассчитать стоимость"
						/>
					</UIShLink>
				</div>

				<NuxtImg
					:src="opt.image"
					:alt="opt.name"
					class="image"
					width="600"
					height="376"
					format="webp"
					loading="lazy"
					decoding="async"
					sizes="sm:60vw xl:100vw"
				/>
			</li>
		</ul>
	</section>
</template>

<script setup lang="ts">
import { formatNumber } from '@/utils/index';
import type { ApiDeliveriesResult } from '@/common/types';

const calcStorage = useCalculatorStorage();
const ymTriggers = useYmTriggers();

const props = defineProps<{
	price: ApiDeliveriesResult | undefined;
}>();

const options = computed(() => {
	const calcValues = calcStorage.values;

	const aviaUrl = new URL('https://avia73.pro/#calc');
	// const aviaUrl = new URL('http://localhost:3001/#calc');
	aviaUrl.searchParams.set('values', JSON.stringify(calcValues.value));

	const autoP = formatNumber(props.price?.auto?.result_cny ?? '0');
	const aviaP = formatNumber(props.price?.avia?.result_cny ?? '0');

	const autoPrice = autoP !== '0' ? autoP : 'от 9,5 ¥ / 1 кг';
	const aviaPrice = aviaP !== '0' ? aviaP : 'от 2,4 ¥ / 1 м³';

	return [
		{
			type: 'avto',
			name: 'Авто доставка',
			description:
				'Позволяет доставлять груз в любой город, так как не нуждается в наличии жд путей и аэропорта. Один из самых популярных способов благодаря своей доступности.',
			price: autoPrice,
			time: 'от 14 дней',
			link: 'https://app73.pro',
			image: '/images/truck.png',
			events: {
				click: () => {
					ymTriggers.hitGoToAuto();
				}
			}
		},
		{
			type: 'avia',
			name: 'Авиа доставка',
			description:
				'Считается самым быстрым способом доставки, используется, как правило, для срочных грузов, так как является одним из надёжных. Этот способ подойдёт для перевозки ценных грузов.',
			price: aviaPrice,
			time: 'от 2 дней',
			// link: `https://avia73.pro/${values ?? ''}#calc`,
			link: aviaUrl.toString(),
			image: '/images/airbus.png',
			events: {
				click: () => {
					ymTriggers.hitGoToAvia();
				}
			}
		}
	];
});
</script>

<style lang="scss" scoped>
.delivery-section {
	margin-top: 4.444rem;
}

.h-text {
	@include useFont(dxsb);
	text-transform: uppercase;
	color: var(--color-white);
	max-width: 18.667rem;

	&2 {
		color: var(--color-gray300);
	}
}

.options {
	display: flex;
	flex-direction: column;
	gap: 0.667rem;
	margin-top: 1.333rem;

	.option {
		width: 100%;
		display: flex;
		flex-direction: column-reverse;
		border-radius: 1.111rem;
		background-color: var(--color-white);
		color: var(--color-gray950);

		.info {
			padding-top: 0;
			padding: 0.889rem;

			h5 {
				@include useFont(txlb);
				text-transform: uppercase;
			}

			p {
				@include useFont(tsmm);
				color: var(--color-gray600);
				margin-top: 0.444rem;
				max-width: 29.778rem;
			}

			.values {
				margin-top: 0.889rem;
				@include useFont(tmds);
				display: flex;
				flex-wrap: nowrap;
				align-items: center;
				gap: 0.667rem;

				li {
					display: flex;
					flex-wrap: nowrap;
					align-items: center;
					gap: 0.333rem;
				}

				.icon {
					color: var(--color-gray300);
				}
			}

			.link {
				width: 100%;
				max-width: 100%;
				margin-top: 1.778rem;

				.btn {
					width: 100%;
				}
			}
		}

		.image {
			width: fit-content;
			height: 100%;
			height: 12rem;
			max-height: 12rem;
			object-fit: cover;
		}
	}
}

@include screen768 {
	.options {
		flex-direction: row;
	}
}

@include screen1920 {
	.delivery-section {
		display: grid;
		grid-template-columns: auto 67.778rem;
		column-gap: 0.667rem;
		margin-top: 7.778rem;
	}

	.h-text {
		max-width: 33.333rem;
		@include useFont(dxlb);
	}

	.options {
		flex-direction: column;
		gap: 1.111rem;
		margin-top: 0;

		.option {
			display: grid;
			column-gap: 1.111rem;
			grid-template-columns: 1fr 33.333rem;
			grid-auto-rows: 20.889rem;
			border-radius: 1.333rem;

			&:last-of-type {
				.image {
					order: -1;
				}
			}

			.info {
				padding: 1.778rem;

				h5 {
					@include useFont(dsmb);
				}

				p {
					@include useFont(txlm);
					margin-top: 0.889rem;
				}

				.values {
					margin-top: 1.111rem;
					@include useFont(txls);
					gap: 2.222rem;

					li {
						gap: 0.444rem;
					}
				}

				.link {
					width: fit-content;
					max-width: fit-content;
					margin-top: 1.778rem;

					.btn {
						width: fit-content;
					}
				}
			}

			.image {
				width: 100%;
				height: 100%;
				max-height: 100%;
				object-fit: cover;
			}
		}
	}
}
</style>
